import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Music2, Video, Lock, ShoppingCart, Gift } from "lucide-react";

export const COVER_IMAGES = [
  "https://images.unsplash.com/photo-1618514272627-962edf3e55a3?w=600&q=80",
  "https://images.unsplash.com/photo-1769643207226-dcc20cbe7d70?w=600&q=80",
  "https://images.unsplash.com/photo-1600320844745-6de494a914ca?w=600&q=80",
  "https://images.unsplash.com/photo-1678216769957-a4a302c8f2d0?w=600&q=80",
  "https://images.unsplash.com/photo-1506157786151-b8491531f063?w=600&q=80",
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=80",
  "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=600&q=80",
  "https://images.unsplash.com/photo-1519377951812-a00ee4d4d6ec?w=600&q=80",
];

export function pickCover(id) {
  return COVER_IMAGES[(id || 0) % COVER_IMAGES.length];
}

export default function MediaCard({ item, size = "md", showProgress = false, fluid = false }) {
  const nav = useNavigate();
  const isAudio = item.IsAudio;
  // Locked = utilizador não pode reproduzir. Duas fontes de verdade:
  //   • `/content/all` → devolve `CanPlay` (0/1) + alias `Owned` (bool).
  //   • `/content/catalog` → devolve `Owned` (bool).
  // Se ambos estiverem `undefined` (ex.: siblings sem enrich), assumimos playable.
  const canPlay = item.CanPlay === true || item.CanPlay === 1
    || item.Owned === true
    || item.IsTeaser === true;
  const explicitLock = item.CanPlay === false || item.CanPlay === 0 || item.Owned === false;
  const locked = explicitLock && !canPlay;
  const cover = pickCover(item.ID_ConteudoMediaCenter);
  // `fluid` mode: ocupa a célula do grid (mobile 2-col ~163px). Sem `fluid`
  // usa larguras fixas — ideal para horizontal scroll rows.
  const width = fluid
    ? "w-full"
    : size === "lg"
    ? "w-[260px] md:w-[320px]"
    : size === "sm"
    ? "w-[160px] md:w-[180px]"
    : "w-[200px] md:w-[240px]";
  const progress = showProgress && item.progress?.duration > 0
    ? Math.min(100, Math.round((item.progress.position / item.progress.duration) * 100))
    : null;

  const handleClick = () => {
    // Em vez de saltar para a loja legada, abre a página do conteúdo —
    // que mostra descrição + botão "Comprar" (adiciona ao carrinho do MY).
    nav(`/media/watch/${item.ID_ConteudoMediaCenter}`);
  };

  return (
    <motion.button
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className={`media-card rounded-xl overflow-hidden text-left flex-shrink-0 group relative ${width}`}
      data-testid={`media-card-${item.ID_ConteudoMediaCenter}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={cover}
          alt={item.Descricao}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${locked ? "opacity-40 grayscale" : "opacity-75 group-hover:opacity-100 group-hover:scale-110"}`}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1829] via-transparent to-transparent" />
        <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-[10px] uppercase tracking-widest text-[#D1CFC9] font-bold">
          {isAudio ? <Music2 size={10} /> : <Video size={10} />}
          {isAudio ? "Áudio" : "Vídeo"}
        </div>
        {locked && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/90 text-[10px] uppercase tracking-wider text-[#0F1829] font-bold" data-testid={`media-lock-${item.ID_ConteudoMediaCenter}`}>
            <Lock size={10} /> Bloqueado
          </div>
        )}
        {item.IsTeaser && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/90 text-[10px] uppercase tracking-wider text-white font-bold" data-testid={`media-teaser-${item.ID_ConteudoMediaCenter}`}>
            <Gift size={10} /> Experimenta
          </div>
        )}
        <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition">
          <div className={`w-14 h-14 rounded-full grid place-items-center shadow-2xl ${locked ? "bg-amber-400 text-[#0F1829]" : "bg-[#D1CFC9] text-[#0F1829]"}`}>
            {locked ? <ShoppingCart size={20} /> : <Play size={22} fill="currentColor" />}
          </div>
        </div>
        {progress !== null && !locked && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
            <div className="h-full bg-[#D1CFC9]" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-medium line-clamp-2 group-hover:text-[#E8E6E2] transition">{item.Descricao}</p>
        {locked && (
          <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-amber-400 font-semibold flex items-center gap-1">
            <ShoppingCart size={10} /> Comprar
          </p>
        )}
      </div>
    </motion.button>
  );
}
